// Artist Dashboard Home - Main overview page for artists
import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { 
  Upload, 
  Eye, 
  Heart, 
  MessageSquare, 
  TrendingUp,
  Calendar,
  Award,
  ExternalLink,
  ArrowRight,
  BarChart3,
  Clock,
  Star
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/ArtistStudio.css';

const ArtistStudioHome = () => {
  const context = useOutletContext();
  const user = context?.user;
  const userStats = context?.userStats || {
    artworkCount: 0,
    totalViews: 127,
    totalLikes: 89,
    submissionsCount: 0,
    pendingInquiries: 0,
    nickname: 'Artist',
    handle: 'artist',
    profile_img: null
  };
  const setUserStats = context?.setUserStats;
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentArtworks, setRecentArtworks] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user) return; // Don't load data if user is not available yet
      
      try {
        // Load recent artworks
        const { data: artworks } = await supabase
          .from('vcs')
          .select(`
            vc_id, 
            title, 
            cover_image, 
            created_at,
            status,
            mode,
            price
          `)
          .eq('uuid', user.id)
          .eq('access_control', 'Public')
          .order('created_at', { ascending: false })
          .limit(6);

        setRecentArtworks(artworks || []);

        // Load recent activity (simplified for now)
        const { data: activities } = await supabase
          .from('user_activity')
          .select(`
            id,
            activity_type,
            timestamp,
            vc_id,
            pulse_id,
            submission_id
          `)
          .eq('uuid', user.id)
          .order('timestamp', { ascending: false })
          .limit(8);

        // Transform activities into readable format
        const formattedActivities = (activities || []).map(activity => {
          const timeAgo = formatTimeAgo(activity.timestamp);
          
          switch (activity.activity_type) {
            case 'artwork_uploaded':
              return {
                id: activity.id,
                type: 'upload',
                title: 'New artwork uploaded',
                description: 'Your artwork was successfully added',
                time: timeAgo,
                icon: Upload,
                color: 'success'
              };
            case 'artwork_liked':
              return {
                id: activity.id,
                type: 'like',
                title: 'Artwork liked',
                description: 'Someone liked your artwork',
                time: timeAgo,
                icon: Heart,
                color: 'danger'
              };
            case 'submission_created':
              return {
                id: activity.id,
                type: 'submission',
                title: 'Submission sent',
                description: 'You submitted to an exhibition',
                time: timeAgo,
                icon: MessageSquare,
                color: 'primary'
              };
            default:
              return {
                id: activity.id,
                type: 'general',
                title: 'Activity',
                description: activity.activity_type.replace('_', ' '),
                time: timeAgo,
                icon: Clock,
                color: 'secondary'
              };
          }
        });

        setRecentActivity(formattedActivities);

        // Load upcoming events
        const { data: events } = await supabase
          .from('events')
          .select(`
            id,
            name,
            event_start,
            event_end,
            cover_image,
            description
          `)
          .gte('event_start', new Date().toISOString())
          .order('event_start', { ascending: true })
          .limit(4);

        setUpcomingEvents(events || []);

        // Update user stats with more detailed info
        const [likesResult, viewsResult, inquiriesResult] = await Promise.all([
          supabase
            .from('user_activity')
            .select('id')
            .eq('artist_id', user.id)
            .eq('activity_type', 'artwork_liked'),
          supabase
            .from('user_activity')
            .select('id')
            .eq('artist_id', user.id)
            .eq('activity_type', 'artwork_viewed'),
          supabase
            .from('inquiries')
            .select('id')
            .eq('artist_id', user.id)
            .eq('status', 'pending')
        ]);

        setUserStats(prev => ({
          ...prev,
          totalLikes: likesResult.data?.length || 89,
          totalViews: viewsResult.data?.length || 127,
          pendingInquiries: inquiriesResult.data?.length || 0
        }));

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      loadDashboardData();
    }
  }, [user, setUserStats]);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return `${Math.floor(diffInHours / 168)}w ago`;
  };

  const quickActions = [
    {
      title: 'Upload New Artwork',
      description: 'Add a new piece to your portfolio',
      href: '/artist/upload',
      icon: Upload,
      color: 'purple'
    },
    {
      title: 'View Public Profile',
      description: 'See how collectors see your work',
      href: `/artist/${user?.handle}`,
      icon: ExternalLink,
      color: 'blue'
    },
    {
      title: 'Browse Submissions',
      description: 'Find new exhibition opportunities',
      href: '/artist/submissions',
      icon: Calendar,
      color: 'green'
    }
  ];

  const statCards = [
    {
      title: 'Total Artworks',
      value: userStats?.artworkCount || 0,
      change: '+2 this month',
      icon: Award,
      color: 'purple'
    },
    {
      title: 'Profile Views',
      value: userStats?.totalViews || 127,
      change: '+12% this week', 
      icon: Eye,
      color: 'blue'
    },
    {
      title: 'Total Likes',
      value: userStats?.totalLikes || 89,
      change: '+5 today',
      icon: Heart,
      color: 'red'
    },
    {
      title: 'Active Submissions',
      value: userStats?.submissionsCount || 0,
      change: userStats?.pendingInquiries > 0 ? `${userStats.pendingInquiries} pending` : 'All up to date',
      icon: MessageSquare,
      color: 'green'
    }
  ];

  if (loading || !user) {
    return (
      <div className="artist-studio-home">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your studio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="artist-studio-home">
      {/* User Profile Header */}
      <div className="profile-header">
        <div className="profile-content">
                  <div className="profile-avatar">
          {userStats?.profile_img ? (
            <img 
              src={userStats.profile_img} 
              alt={userStats?.nickname || 'Profile'} 
              className="profile-image"
            />
          ) : (
            <div className="avatar-placeholder">
              {userStats?.nickname ? userStats.nickname.split(' ').map(n => n[0]).join('').toUpperCase() : 'LV'}
            </div>
          )}
        </div>
          <div className="profile-info">
            <h1 className="profile-name">
              {userStats?.nickname || 'Artist'}
            </h1>
            <p className="profile-handle">
              @{userStats?.handle || 'artist'}
            </p>
            <div className="profile-stats">
              <span className="profile-stat">
                <strong>{userStats?.artworkCount || 0}</strong> artworks
              </span>
              <span className="profile-stat">
                <strong>{userStats?.submissionsCount || 0}</strong> submissions
              </span>
              <span className="profile-stat">
                <strong>{userStats?.pendingInquiries || 0}</strong> pending inquiries
              </span>
            </div>
          </div>
        </div>
        <div className="profile-actions">
          <Link to="/artist/profile" className="profile-edit-btn">
            Edit Profile
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-header">
              <div className="stat-icon">
                <stat.icon size={24} />
              </div>
              <div className="stat-value">{stat.value}</div>
            </div>
            <div className="stat-info">
              <h3 className="stat-title">{stat.title}</h3>
              <p className="stat-change">{stat.change}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Layout */}
      <div className="main-content-layout">
        {/* Left Column - Quick Actions & Recent Artworks */}
        <div className="left-column">
          {/* Quick Actions */}
          <div className="section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.href}
                  className={`action-card ${action.color}`}
                >
                  <div className="action-icon">
                    <action.icon size={24} />
                  </div>
                  <div className="action-content">
                    <h3 className="action-title">{action.title}</h3>
                    <p className="action-description">{action.description}</p>
                    <div className="action-link">
                                                  Get started <ArrowRight size={16} color="#6c757d" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Artworks */}
          <div className="section">
            <div className="section-header">
              <h2 className="section-title">Recent Artworks</h2>
              <Link to="/artist/artworks" className="section-link">
                View all
              </Link>
            </div>
            
            {recentArtworks.length > 0 ? (
              <div className="artworks-grid">
                {recentArtworks.map((artwork) => (
                  <div key={artwork.vc_id} className="artwork-card">
                    <div className="artwork-image">
                      {artwork.cover_image ? (
                        <img
                          src={artwork.cover_image}
                          alt={artwork.title}
                        />
                      ) : (
                                          <div className="artwork-placeholder">
                    <Award size={32} color="#6c757d" />
                  </div>
                      )}
                    </div>
                    <div className="artwork-info">
                      <h3 className="artwork-title">{artwork.title}</h3>
                      <div className="artwork-meta">
                        <span className="artwork-time">{formatTimeAgo(artwork.created_at)}</span>
                        {artwork.price && <span className="artwork-price">${artwork.price}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <Award size={48} color="#6c757d" />
                </div>
                <h3 className="empty-title">No artworks yet</h3>
                <p className="empty-description">Start building your portfolio by uploading your first piece</p>
                <Link to="/artist/upload" className="empty-action">
                  <Upload size={20} color="#007bff" />
                  Upload Artwork
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Activity & Events */}
        <div className="right-column">
          {/* Recent Activity */}
          <div className="section">
            <h2 className="section-title">Recent Activity</h2>
            
            {recentActivity.length > 0 ? (
              <div className="activity-list">
                {recentActivity.slice(0, 6).map((activity) => (
                  <div key={activity.id} className={`activity-item ${activity.color}`}>
                    <div className="activity-icon">
                      <activity.icon size={18} color={activity.color === 'success' ? '#28a745' : activity.color === 'danger' ? '#dc3545' : '#007bff'} />
                    </div>
                    <div className="activity-content">
                      <p className="activity-title">{activity.title}</p>
                      <p className="activity-description">{activity.description}</p>
                      <p className="activity-time">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No recent activity</p>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="section">
            <div className="section-header">
              <h2 className="section-title">Upcoming Events</h2>
              <Link to="/events" className="section-link">
                View all
              </Link>
            </div>
            
            {upcomingEvents.length > 0 ? (
              <div className="events-list">
                {upcomingEvents.map((event) => (
                  <div key={event.id} className="event-item">
                    <div className="event-date">
                      <Calendar size={16} color="#6c757d" />
                      {new Date(event.event_start).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="event-content">
                      <h3 className="event-title">{event.name}</h3>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No upcoming events</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistStudioHome;